import { Badge } from "@components/ui/Badge"
import { formatDate } from "@lib/intl"
import { DEFAULT_TYPE_ICON, TYPE_ICONS } from "@lib/materials"
import { CalendarClock, ChevronRight } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import type { HomeAgenda as Agenda, HomeAgendaItem } from "@/types"

interface Props {
  agenda: Agenda
}

/**
 * Today (PRD-59 §FR-6).
 *
 * Every other number on this page is cumulative and undated, which made the
 * one question a professor opens the app to ask — "¿qué pasa hoy?" —
 * unanswerable. A deadline that passed this morning was pooled with one in
 * March; a send the 08:00 cron will perform in twenty minutes was suppressed
 * on purpose, on the grounds that a dated material "needs nothing from the
 * professor". True for action, false for reassurance: "el examen sale a 2°A a
 * las 8:00" is the highest-stakes fact in the system.
 *
 * This band leads the page and renders nothing when the day is clear — an
 * empty agenda is itself the answer, delivered by the panel below.
 */
export const HomeAgenda = memo(function HomeAgenda({ agenda }: Props) {
  const { t } = useTranslation("home")
  if (agenda.items.length === 0) return null

  return (
    <section
      aria-labelledby="home-agenda-title"
      className="rounded-card border-border bg-surface border"
    >
      <header className="border-hairline flex items-center gap-2 border-b px-4 py-3">
        <CalendarClock className="text-accent h-4 w-4 shrink-0" aria-hidden />
        <h2 id="home-agenda-title" className="text-ink text-sm font-semibold">
          {t("agenda.title", { date: formatDate(agenda.today, "EEEE d 'de' MMMM") })}
        </h2>
      </header>
      <ul className="divide-hairline divide-y">
        {agenda.items.map((item) => (
          <AgendaRow key={`${item.kind}-${item.material_id}`} item={item} />
        ))}
      </ul>
    </section>
  )
})

function AgendaRow({ item }: { item: HomeAgendaItem }) {
  const { t } = useTranslation("home")
  const Icon = TYPE_ICONS[item.material_type] ?? DEFAULT_TYPE_ICON

  // What this row is asking of the professor, in her words — never the
  // internal kind. A send is the only one she can still stop, so it leads.
  const detail =
    item.kind === "send_today"
      ? t("agenda.send_today")
      : item.kind === "due_today"
        ? t("agenda.due_today", { count: item.pending_count })
        : t("agenda.overdue", {
            count: item.pending_count,
            date: formatDate(item.date, "d MMM"),
          })

  return (
    <li>
      <Link
        to={`/materiales/${item.material_id}`}
        className="hover:bg-inset flex min-h-11 items-center gap-3 px-4 py-2.5 transition-colors"
      >
        <Icon className="text-ink-subtle h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="text-ink block truncate text-sm font-medium">{item.material_title}</span>
          <span className="text-meta block truncate">
            {item.classroom_name} · {detail}
          </span>
        </span>
        {item.kind === "overdue" ? (
          <Badge variant="attention">{t("agenda.badge_overdue")}</Badge>
        ) : item.kind === "send_today" ? (
          <Badge variant="accent">{t("agenda.badge_send")}</Badge>
        ) : null}
        <ChevronRight className="text-ink-subtle h-4 w-4 shrink-0" aria-hidden />
      </Link>
    </li>
  )
}
